import { computed, unref, type Ref } from 'vue';
import Dinero from 'dinero.js';
import { useSessionStore, useAppStore } from '@/store';

type OrderItemId = number | string;

export function usePriceBreakdown(options?: { orderItemIds?: Ref<OrderItemId[]> | OrderItemId[] }) {
    const session = useSessionStore();
    const appStore = useAppStore();

    const orderItemIds = computed<OrderItemId[] | undefined>(() => {
        return (options && options.orderItemIds ? unref(options.orderItemIds) : undefined) as OrderItemId[] | undefined;
    });

    const breakdownItems = computed(() => {
        const items: Array<{
            label: string;
            unitCost: string | null;
            rows: Array<{ label: string; value: string; muted?: boolean }>;
        }> = [];

        const orderItems = (session.order as any)?.items ?? [];

        let confirmableItems = orderItems.filter(
            (item: any) => item?.status !== 'CONFIRMED' || item?.service_status === 'Pending',
        );

        const ids = unref(orderItemIds);
        if (ids && ids.length) {
            const idSet = new Set(ids);
            confirmableItems = confirmableItems.filter((i: any) => idSet.has(i.id));
        }

        // Exclude storage when any non-storage items are present
        const hasNonStorage = confirmableItems.some(
            (i: any) => ((i?.productable_type ?? '') as string).toLowerCase() !== 'storage',
        );
        if (hasNonStorage) {
            confirmableItems = confirmableItems.filter(
                (i: any) => ((i?.productable_type ?? '') as string).toLowerCase() !== 'storage',
            );
        }

        // Helpers
        const formatDate = (iso: string | null | undefined, tz?: string | null): string => {
            if (!iso) return '-';
            try {
                const date = new Date(iso);
                const formatter = new Intl.DateTimeFormat('en-AU', {
                    timeZone: tz ?? undefined,
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                });
                return formatter.format(date);
            } catch {
                return '-';
            }
        };

        const deriveUnit = (product: any): string | null => {
            return product?.price_unit_formatted;
        };

        confirmableItems.forEach((orderItem: any) => {
            const label = orderItem.service_label ?? orderItem.service_type ?? 'Service';
            let unitCost: string | null = null;
            let isVariable = false;
            let isMove = false;

            // STORAGE handling
            const isStorage = ((orderItem.productable_type ?? '') as string).toLowerCase() === 'storage';
            if (isStorage) {
                const product = orderItem.product ?? {};
                const unit = deriveUnit(product);
                if (product?.price?.formatted && unit) {
                    unitCost = `${product.price.formatted} / ${unit}`;
                } else if (product?.price?.formatted) {
                    unitCost = product.price.formatted;
                }

                const start = formatDate(product?.start_date, product?.timezone?.timezone);
                const end = formatDate(product?.end_date, product?.timezone?.timezone);

                items.push({
                    label,
                    unitCost,
                    rows: [
                        { label: 'Start date', value: start },
                        { label: 'End date', value: end },
                    ],
                });
                return; // proceed next item
            }

            if ((orderItem.productable_type ?? '').toLowerCase() === 'move') {
                const selectedMatch = session.getSelectedMatch;
                const pricingType = selectedMatch?.pricing_type ?? null;
                isVariable = pricingType === 'VARIABLE';
                isMove = true;

                if (session.isLocal && selectedMatch?.price_per_unit?.formatted) {
                    if (selectedMatch.price_per_hour) {
                        unitCost = `${selectedMatch.price_per_hour.formatted} / hr`;
                    } else {
                        unitCost = `${selectedMatch.price_per_unit.formatted} / 30 mins`;
                    }
                } else if (!isVariable && orderItem.totals?.TOTAL?.formatted) {
                    unitCost = orderItem.totals.TOTAL.formatted;
                }
            } else {
                const product = orderItem.product ?? {};
                const basePrice: string | null =
                    product?.price?.formatted ?? orderItem.totals?.TOTAL?.formatted ?? null;
                const priceUnit: string | null = product?.price_unit ?? null;
                const priceUnitFormatted: any = product?.price_unit_formatted ?? null;

                // Determine variable status for non-move items
                const productPriceType: string | null = product?.price_type ?? null;
                const depositType: string | null = product?.deposit_type ?? null;
                const quantity: number | null = product?.quantity ?? null;
                isVariable = productPriceType === 'VARIABLE' || depositType === 'METERED' || !!quantity;

                if (basePrice) {
                    if (priceUnit === 'HALF_HOUR') {
                        unitCost = `${basePrice} / 30 mins`;
                    } else if (priceUnitFormatted?.unit_single) {
                        unitCost = `${basePrice} / ${priceUnitFormatted.unit_single}`;
                    } else {
                        unitCost = basePrice;
                    }
                } else {
                    unitCost = null;
                }
            }

            const remaining: string | null = isVariable ? null : (orderItem.totals?.BALANCE?.formatted ?? null);
            // For interstate moves not within a week, show note next to label
            const remainingLabelNote: string | null =
                isMove && !session.isLocal && session.getSelectedMatch?.within_week != 1 ?
                    ' (5 days before move)'
                :   null;
            const remainingNote = isVariable ? 'To be calculated after service complete' : null;

            const rows: Array<{ label: string; value: string; muted?: boolean }> = [];

            // For Move items, add pricing breakdown sections
            if (isMove) {
                const selectedMatch = session.getSelectedMatch;
                const hasSurcharge = selectedMatch?.surcharge?.amount && Number(selectedMatch.surcharge.amount) > 0;

                // Only show Space price and Access/additional fees when surcharge exists
                if (hasSurcharge) {
                    // Space price
                    const spacePrice = selectedMatch?.space_total?.formatted ?? null;
                    rows.push({ label: 'Space price', value: spacePrice ?? '-' });

                    // Access/additional fees
                    rows.push({ label: 'Additional fees', value: selectedMatch.surcharge.formatted });
                }

                // Deposit
                rows.push({
                    label: 'Deposit (due now)',
                    value: (orderItem.totals?.DEPOSIT?.formatted ?? '-') as string,
                });

                if (selectedMatch?.pricing_type === 'VARIABLE') {
                    rows.push({
                        label: 'Minimum hours',
                        value: Number(appStore.tenantDepositHours).toFixed(0) + ' hours',
                    });
                }

                // Final payment (renamed from Remaining)
                if (remainingNote) {
                    const lbl = remainingLabelNote ? `Final payment${remainingLabelNote}` : 'Final payment';
                    rows.push({ label: lbl, value: remainingNote, muted: true });
                } else {
                    const lbl = remainingLabelNote ? `Final payment${remainingLabelNote}` : 'Final payment';
                    rows.push({ label: lbl, value: (remaining ?? '-') as string });
                }
            } else {
                // For non-Move items, keep existing structure
                // Deposit row
                rows.push({
                    label: 'Deposit (due now)',
                    value: (orderItem.totals?.DEPOSIT?.formatted ?? '-') as string,
                });
                // Remaining row
                if (remainingNote) {
                    const lbl = remainingLabelNote ? `Remaining${remainingLabelNote}` : 'Remaining';
                    rows.push({ label: lbl, value: remainingNote, muted: true });
                } else {
                    const lbl = remainingLabelNote ? `Remaining${remainingLabelNote}` : 'Remaining';
                    rows.push({ label: lbl, value: (remaining ?? '-') as string });
                }
            }

            items.push({
                label,
                unitCost,
                rows,
            });
        });

        return items;
    });

    const dueNowTotal = computed(() => {
        const computeDueNow = (ids?: Array<number | string>) => {
            const orderItems = (session.order as any)?.items ?? [];
            const idSet = ids && ids.length > 0 ? new Set(ids) : null;
            const total = orderItems
                .filter((item: any) => item?.status !== 'CONFIRMED')
                // Exclude storage from due now
                .filter((item: any) => ((item?.productable_type ?? '') as string).toLowerCase() !== 'storage')
                .filter((item: any) => (idSet ? idSet.has(item.id) : true))
                .reduce((acc: number, item: any) => acc + Number.parseInt(item?.totals?.DEPOSIT?.amount ?? 0), 0);
            return Dinero({ amount: total }).toFormat('$0,0.00');
        };

        const ids = orderItemIds.value as Array<number | string> | undefined;
        return computeDueNow(ids);
    });

    return {
        breakdownItems,
        dueNowTotal,
    };
}
